import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Crown } from "lucide-react";
import { PLANS, SINGLES_PLANS } from "@/lib/data";
import { CheckoutModal } from "@/components/CheckoutModal";

const FEATURES_DOUBLES = [
    "Daily doubles delivered to you",
    "Professional match analysis",
    "Subscriber-only access",
    "Full records archive",
];

const FEATURES_SINGLES = [
    "Daily singles with odds 1.90 – 2.50",
    "Professional match analysis",
    "Subscriber-only access",
    "Full records archive",
];

export const Plans = () => {
    const [selected, setSelected] = useState(null);

    return (
        <section id="subscription" data-testid="subscription-plans-section" className="relative py-20 lg:py-32 bg-[#0B0F17]/50 overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] bg-bn-blue/10 blur-[120px] rounded-full" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-14"
                >
                    <div className="font-mono text-[11px] tracking-[0.3em] text-bn-blue font-semibold mb-4">SUBSCRIPTION PLANS</div>
                    <h2 data-testid="subscription-plans-heading" className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">
                        Choose your <span className="text-bn-blue text-glow-blue">edge</span>
                    </h2>
                    <p className="mt-4 text-base text-slate-400 max-w-xl mx-auto">
                        Every plan includes the same professional daily double — pick the volume that fits your bankroll strategy.
                    </p>
                </motion.div>

                <div className="font-mono text-[11px] tracking-[0.3em] text-bn-cyan font-semibold mb-6 text-center">OFFER 1 — SINGLES · ODDS 1.90 – 2.50</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
                    {SINGLES_PLANS.map((p, i) => (
                        <motion.div
                            key={p.id}
                            data-testid={`plan-card-${p.id}`}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            className={`relative flex flex-col rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1.5 ${
                                p.recommended
                                    ? "bg-gradient-to-b from-bn-card to-[#0D1526] border-2 border-bn-blue animate-pulse-subtle lg:scale-[1.04] z-10"
                                    : "bg-bn-card/70 border border-bn-line hover:border-bn-blue/40"
                            }`}
                        >
                            {p.recommended && (
                                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-bn-blue text-white font-mono text-[10px] font-bold tracking-[0.15em] shadow-[0_0_20px_rgba(0,102,255,0.6)]">
                                    <Crown className="w-3 h-3" /> RECOMMENDED
                                </div>
                            )}
                            <div className="font-mono text-[11px] tracking-[0.25em] text-slate-400 font-semibold">{p.name}</div>
                            <div className="mt-4 flex items-baseline gap-2">
                                <span className="font-display font-extrabold text-4xl text-white">€{p.price}</span>
                            </div>
                            <div className="mt-1 font-mono text-xs text-bn-cyan">{p.per}</div>
                            <ul className="mt-7 space-y-3 flex-1">
                                {FEATURES_SINGLES.map((f) => (
                                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                                        <Check className="w-4 h-4 text-bn-green shrink-0 mt-0.5" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <button
                                data-testid={`btn-subscribe-${p.id}`}
                                onClick={() => setSelected(p)}
                                className={`mt-8 w-full py-3.5 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 ${
                                    p.recommended
                                        ? "bg-bn-blue hover:bg-bn-bluehover text-white hover:shadow-[0_0_30px_rgba(0,102,255,0.6)]"
                                        : "border border-white/15 text-slate-200 hover:border-bn-blue hover:bg-bn-blue/10 hover:text-white"
                                }`}
                            >
                                SUBSCRIBE NOW
                            </button>
                        </motion.div>
                    ))}
                </div>

                <div className="font-mono text-[11px] tracking-[0.3em] text-bn-cyan font-semibold mt-16 mb-6 text-center">OFFER 2 — DAILY DOUBLES · ODDS ABOVE 2.50</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
                    {PLANS.map((p, i) => (
                        <motion.div
                            key={p.id}
                            data-testid={`plan-card-${p.id}`}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            className={`relative flex flex-col rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1.5 ${
                                p.recommended
                                    ? "bg-gradient-to-b from-bn-card to-[#0D1526] border-2 border-bn-blue animate-pulse-subtle lg:scale-[1.04] z-10"
                                    : "bg-bn-card/70 border border-bn-line hover:border-bn-blue/40"
                            }`}
                        >
                            {p.recommended && (
                                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-bn-blue text-white font-mono text-[10px] font-bold tracking-[0.15em] shadow-[0_0_20px_rgba(0,102,255,0.6)]">
                                    <Crown className="w-3 h-3" /> RECOMMENDED
                                </div>
                            )}
                            <div className="font-mono text-[11px] tracking-[0.25em] text-slate-400 font-semibold">{p.name}</div>
                            <div className="mt-4 flex items-baseline gap-2">
                                <span className="font-display font-extrabold text-4xl text-white">€{p.price}</span>
                            </div>
                            <div className="mt-1 font-mono text-xs text-bn-cyan">{p.per}</div>
                            <ul className="mt-7 space-y-3 flex-1">
                                {FEATURES_DOUBLES.map((f) => (
                                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                                        <Check className="w-4 h-4 text-bn-green shrink-0 mt-0.5" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <button
                                data-testid={`btn-subscribe-${p.id}`}
                                onClick={() => setSelected(p)}
                                className={`mt-8 w-full py-3.5 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 ${
                                    p.recommended
                                        ? "bg-bn-blue hover:bg-bn-bluehover text-white hover:shadow-[0_0_30px_rgba(0,102,255,0.6)]"
                                        : "border border-white/15 text-slate-200 hover:border-bn-blue hover:bg-bn-blue/10 hover:text-white"
                                }`}
                            >
                                SUBSCRIBE NOW
                            </button>
                        </motion.div>
                    ))}
                </div>

                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="mt-10 text-center text-xs text-slate-500 max-w-2xl mx-auto"
                >
                    Betting involves financial risk. Past results do not guarantee future results. 18+ only — please bet responsibly.
                </motion.p>
            </div>
            <CheckoutModal plan={selected} onClose={() => setSelected(null)} />
        </section>
    );
};
